import React, { useState, useEffect, useRef, CSSProperties } from "react";
import styles from "./CollapsibleMessage.module.scss";
import Locale from "@/app/locales";

interface CollapsibleMessageProps {
  children: React.ReactNode;
  maxHeight?: number; // 最大高度，单位像素
  expandText?: string; // 展开按钮文字
  collapseText?: string; // 收起按钮文字
  className?: string; // 自定义容器类名
  gradientColor?: string; // 渐变遮罩颜色
  hideCollapseText?: boolean; // 是否隐藏收起
  useCollapse?: boolean;
}

const CollapsibleMessage: React.FC<CollapsibleMessageProps> = ({
  children,
  maxHeight = 150,
  expandText = Locale.chat.expandText,
  collapseText = Locale.chat.collapseText,
  className = "",
  gradientColor = "rgba(255, 255, 255, 1)",
  hideCollapseText = true,
  useCollapse = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  if (!useCollapse) {
    // 如果不使用收起展开功能，则最大高度为无限大
    maxHeight = Number.MAX_SAFE_INTEGER;
  }

  // 创建CSS变量供CSS模块使用
  const cssVariables: CSSProperties = {
    "--max-height": `${maxHeight}px`,
    "--gradient-color": gradientColor,
  } as CSSProperties;

  // 检查内容是否超过最大高度
  useEffect(() => {
    if (contentRef.current) {
      const isOverflowing = contentRef.current.scrollHeight > maxHeight;
      setIsOverflow(isOverflowing);
    }
  }, [children, maxHeight]);

  // 在内容变化时，如果不需要折叠则恢复到收起状态
  useEffect(() => {
    if (!isOverflow) {
      setIsExpanded(false);
    }
  }, [isOverflow]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const contentClasses =
    isOverflow && !isExpanded
      ? styles.contentCollapsed
      : styles.contentExpanded;

  return (
    <div className={`${styles.container} ${className}`} style={cssVariables}>
      <div className={contentClasses} ref={contentRef}>
        {children}
      </div>

      {isOverflow && !isExpanded && <div className={styles.mask} />}

      {isOverflow && (
        <button
          onClick={toggleExpand}
          className={styles.toggleButton}
          aria-label={isExpanded ? collapseText : expandText}
        >
          {isExpanded ? (
            !hideCollapseText && (
              <>
                {collapseText}
                <svg
                  className={styles.icon}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 15l7-7 7 7"
                  ></path>
                </svg>
              </>
            )
          ) : (
            <>
              {expandText}
              <svg
                className={styles.icon}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                ></path>
              </svg>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default CollapsibleMessage;
